import { DestroyRef, Injectable, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { interval, firstValueFrom } from 'rxjs';
import { filter, catchError } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from './dialog/dialog.service';
import { DialogResult } from '../interfaces/dialog.interface';
import { VersionUpdateDialogComponent, VersionUpdateData } from './dialog/version-update-dialog/version-update-dialog.component';
import { RumInitializerService } from './rum/rum-initializer.service';
import packageJson from '../../../package.json';

export interface VersionInfo {
  version: string;
  buildDate: string;
  environment: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private http = inject(HttpClient);
  private dialogService = inject(DialogService);
  private rumService = inject(RumInitializerService);

  private currentVersion = packageJson.version;
  private checkInterval = 5 * 60 * 1000; // 5 minutos

  // Signals para gestión de estado
  private isCheckingSignal = signal<boolean>(false);
  private versionInfoSignal = signal<VersionInfo | null>(null);

  // Signals públicos de solo lectura
  public readonly isChecking = this.isCheckingSignal.asReadonly();
  public readonly versionInfo = this.versionInfoSignal.asReadonly();

  // Computed signals para casos de uso adicionales
  public readonly hasNewVersionAvailable = computed(() => {
    const info = this.versionInfo();
    return info ? this.compareVersions(info.version, this.currentVersion) : false;
  });

  public readonly isCheckingText = computed(() =>
    this.isChecking() ? 'Verificando actualizaciones...' : ''
  );

  // Observables para compatibilidad con código existente
  public isChecking$ = toObservable(this.isChecking);
  public versionInfo$ = toObservable(this.versionInfo);

  private destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);

  /**
   * Inicia la verificación periódica de versión
   * Se ejecuta solo si no está en modo de desarrollo
   */
  public initVersionCheck(): void {
    if (!this.isProduction()) {
      return;
    }

    // Verificar inmediatamente
    this.checkVersion();

    // Verificar periódicamente con cleanup via takeUntilDestroyed
    interval(this.checkInterval)
      .pipe(
        filter(() => !this.isChecking()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.checkVersion();
      });
  }

  /**
   * Verifica si hay una nueva versión disponible
   */
  public async checkVersion(): Promise<boolean> {
    if (this.isChecking()) {
      return false;
    }

    this.isCheckingSignal.set(true);

    try {
      const versionInfo = await this.getServerVersion();

      if (versionInfo) {
        this.versionInfoSignal.set(versionInfo);

        const hasNewVersion = this.compareVersions(versionInfo.version, this.currentVersion);
        if (hasNewVersion) {
          this.rumService.recordEvent('version_update_detected', {
            current: this.currentVersion,
            server: versionInfo.version
          });
          this.promptForUpdate(versionInfo);
          return true;
        }
      }

      return false;
    } catch (error) {
      this.rumService.recordError(error);
      return false;
    } finally {
      this.isCheckingSignal.set(false);
    }
  }

  /**
   * Obtiene la información de versión del servidor
   */
  private async getServerVersion(): Promise<VersionInfo | null> {
    try {
      const timestamp = new Date().getTime();
      const response = await firstValueFrom(
        this.http.get<VersionInfo>(`/assets/version.json?v=${timestamp}`, {
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
            'X-Skip-Loading': 'true'
          }
        }).pipe(
          catchError(() => {
            return Promise.resolve(this.getVersionFromMetaTag());
          })
        )
      );

      return response;
    } catch {
      return this.getVersionFromMetaTag();
    }
  }

  /**
   * Obtiene la versión desde el meta tag como fallback
   */
  private getVersionFromMetaTag(): VersionInfo {
    const versionMeta = document.querySelector('meta[name="version"]');
    const timestampMeta = document.querySelector('meta[name="build-timestamp"]');

    return {
      version: versionMeta?.getAttribute('content') || this.currentVersion,
      buildDate: timestampMeta?.getAttribute('content') || new Date().toISOString(),
      environment: this.isProduction() ? 'production' : 'development'
    };
  }

  /**
   * Muestra prompt para actualizar la aplicación
   */
  private promptForUpdate(versionInfo: VersionInfo): void {
    const data: VersionUpdateData = {
      newVersion: versionInfo.version,
      currentVersion: this.currentVersion,
      buildDate: versionInfo.buildDate
    };

    this.dialog.open(VersionUpdateDialogComponent, {
      data,
      width: '420px',
      disableClose: false,
      panelClass: 'gradient-header-dialog'
    }).afterClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((result: DialogResult | undefined) => {
      if (result?.confirmed) {
        this.rumService.recordEvent('version_update_accepted', {
          from: this.currentVersion,
          to: versionInfo.version
        });
        setTimeout(() => this.forceUpdate(), 1000);
      }
    });
  }

  /**
   * Fuerza la actualización de la aplicación con cache busting completo
   */
  public async forceUpdate(): Promise<void> {
    try {
      // 1. Desregistrar cualquier service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // 2. Limpiar todo el cache del service worker y navegador
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // 3. Limpiar sessionStorage
      sessionStorage.clear();

      // 4. Limpiar localStorage selectivamente (mantener datos de auth)
      const localStorageKeysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !key.includes('Cognito') && !key.includes('aws') && !key.includes('Auth')) {
          localStorageKeysToRemove.push(key);
        }
      }
      localStorageKeysToRemove.forEach(key => localStorage.removeItem(key));

      // 5. Usar location.href con parámetros para forzar bypass del cache
      const baseUrl = window.location.origin + window.location.pathname;
      const timestamp = new Date().getTime();
      const random = Math.random().toString(36).substring(7);
      window.location.href = `${baseUrl}?_=${timestamp}&cb=${random}&force=true`;

    } catch (error) {
      this.rumService.recordError(error);

      // Fallback: hard refresh
      try {
        window.location.reload();
      } catch {
        const baseUrl = window.location.origin + window.location.pathname;
        const timestamp = new Date().getTime();
        window.location.replace(`${baseUrl}?force=${timestamp}`);
      }
    }
  }

  /**
   * Verificación manual de versión (para usar en menú o botón)
   */
  public async checkNow(): Promise<boolean> {
    const hasUpdate = await this.checkVersion();

    if (!hasUpdate) {
      this.dialogService.info(
        `Aplicación actualizada\n\nVersión actual: ${this.currentVersion}\nNo hay actualizaciones disponibles.`
      ).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    }

    return hasUpdate;
  }

  /**
   * Obtiene la versión actual
   */
  public getCurrentVersion(): string {
    return this.currentVersion;
  }

  /**
   * Compara dos versiones para determinar si hay una nueva versión
   */
  private compareVersions(serverVersion: string, currentVersion: string): boolean {
    if (serverVersion === currentVersion) {
      return false;
    }

    const parseVersion = (version: string) => {
      const parts = version.split('.').map(num => parseInt(num, 10) || 0);
      return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0 };
    };

    const server = parseVersion(serverVersion);
    const current = parseVersion(currentVersion);

    if (server.major !== current.major) {
      return server.major > current.major;
    }
    if (server.minor !== current.minor) {
      return server.minor > current.minor;
    }
    if (server.patch !== current.patch) {
      return server.patch > current.patch;
    }

    return true;
  }

  /**
   * Verifica si está en modo producción
   */
  private isProduction(): boolean {
    return !window.location.hostname.includes('localhost') &&
           !window.location.hostname.includes('127.0.0.1') &&
           !window.location.hostname.includes('dev');
  }

  /**
   * Obtiene información detallada de la aplicación
   */
  public getAppInfo(): { version: string; buildDate: string; environment: string } {
    const versionInfo = this.versionInfo();
    return {
      version: this.currentVersion,
      buildDate: versionInfo?.buildDate || 'Desconocida',
      environment: this.isProduction() ? 'Producción' : 'Desarrollo'
    };
  }

}
